// frontend/src/utils/canonicalUrl.js
//
// The single definition of "what absolute URL does this route actually serve
// 200 on". Dependency-free on purpose.
//
// WHY IT IS NOT IN components/Seo.js (where it used to live): URL construction
// is needed by content modules (content/blog/blogIndex.js) and by structured
// data, not just by the <Seo> component. Importing it from the component made
// content/ depend on components/, and - concretely - broke any test that does
// `jest.mock("../components/Seo")`, because mocking the COMPONENT silently
// removed the URL builder too. Seo.js re-exports both names, so every existing
// `import Seo, { SITE_URL } from ".../Seo"` keeps working unchanged.

export const SITE_URL = "https://leadifyai.biz";

/**
 * Build the canonical absolute URL for a route path, always in the
 * TRAILING-SLASH form. The prerender writes each route to
 * build/<route>/index.html, which the GCS bucket serves at HTTP 200 only for
 * the trailing-slash URL (`/accessibility/`); the no-slash URL 301-redirects
 * to `/accessibility/index.html` (the app then rewrites the address to
 * `/accessibility/` - utils/indexHtmlUrl.js), which Google reports as "Page
 * with redirect" and indexes slowly.
 * Emitting the trailing-slash form here keeps canonical + og:url on the exact
 * 200 URL the sitemap lists. The homepage stays "/".
 *
 * Structured data (JSON-LD @id / breadcrumb items) and share links must resolve
 * to the SAME string as the canonical tag; the only way to guarantee that is to
 * build them from this function rather than hand-concatenating SITE_URL.
 *
 * Segment splitting (rather than a trim regex) makes the result double-slash
 * safe: a base or slug that already carries a slash cannot produce "//".
 *
 * @param {string} path Route path (e.g. "/", "/blog", "/blog/some-slug").
 * @returns {string} Absolute canonical URL, trailing-slash normalised.
 */
export function buildCanonicalUrl(path) {
  const segments = String(path || "").split("/").filter(Boolean);
  if (!segments.length) return `${SITE_URL}/`;
  return `${SITE_URL}/${segments.join("/")}/`;
}
